/**
 * UAT 2026-05-13 — NeedsAttentionCard.
 *
 * Dashboard surface for SLA / Hour Package alerts (Phase 39 dispatch). Shows
 * the 5 most recent unread notifications with kind label pill, project name,
 * threshold/tier detail, inline action links (Open project / View timesheet),
 * and an explicit green "Solve" button — clicking Solve is the only path
 * that marks a notification as read (manager confirmation, not passive).
 *
 * Polls every 60s via useNotifications (matches useApprovalCounts cadence).
 * Display logic and payload mapping live in `utils/notificationDisplay.ts`
 * so the full Notifications page can reuse the same shape.
 */
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { useNotifications, useMarkNotificationRead } from '../../hooks/useNotifications';
import {
  KIND_TONE,
  describePayload,
  notificationKindLabel,
  timesheetReportHref,
} from '../../utils/notificationDisplay';
import { useFlag } from '../../services/flagsContext';

export function NeedsAttentionCard() {
  const { data, isLoading } = useNotifications({ unread: true, limit: 5 });
  const markRead = useMarkNotificationRead();
  // MKS-556 — the crossed percentage in the kind pill.
  const thresholdPctInLabel = useFlag('mks_556_highest_threshold_only');

  const unreadCount = data?.unreadCount ?? 0;
  const rows = data?.data ?? [];

  return (
    <Card
      title={`Needs your attention${unreadCount > 0 ? ` · ${unreadCount}` : ''}`}
      action={
        <Link
          to="/notifications"
          className="text-[11.5px] text-ms-text-secondary hover:text-ms-text-strong hover:underline"
        >
          View all
        </Link>
      }
    >
      {isLoading ? (
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12px] text-ms-text-muted">All caught up — nothing new.</div>
      ) : (
        <ul className="divide-y divide-ms-border">
          {rows.map((n) => {
            // Defensive fallback for notification kinds not in the display
            // maps (e.g. task.assigned / kudos_received / intake.nag) — an
            // unmapped kind must not crash the whole dashboard card. Mirrors
            // NotificationsPage.tsx.
            const tone = KIND_TONE[n.kind] ?? { bg: '#F4F1EC', fg: '#5C5C68' };
            const kindLabel = notificationKindLabel(n.kind, n.payload, thresholdPctInLabel);
            const { projectName, projectId, taskId, detail, href } = describePayload(n.payload);
            // MKS-144 UAT — task.* rows link to the full-page task view,
            // not the project (mirrors NotificationsPage).
            const titleHref =
              taskId != null
                ? `/tasks/${taskId}`
                : projectId != null
                  ? `/projects/${projectId}`
                  : (href ?? null);
            return (
              <li key={n.id} className="py-3">
                <div className="mb-1.5">
                  <Pill bg={tone.bg} fg={tone.fg}>
                    {kindLabel}
                  </Pill>
                </div>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    {titleHref != null ? (
                      <Link
                        to={titleHref}
                        className="block text-[13px] font-semibold text-ms-text-strong hover:underline truncate"
                      >
                        {projectName}
                        {detail ? (
                          <span className="ml-1.5 text-[11.5px] font-normal text-ms-text-secondary">
                            · {detail}
                          </span>
                        ) : null}
                      </Link>
                    ) : (
                      <div className="text-[13px] font-semibold text-ms-text-strong truncate">
                        {projectName}
                      </div>
                    )}
                    {projectId != null && (
                      <div className="mt-0.5 flex items-center gap-2 text-[11px]">
                        {taskId != null && (
                          <>
                            <Link
                              to={`/tasks/${taskId}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              Task
                            </Link>
                            <span className="text-ms-border">·</span>
                          </>
                        )}
                        <Link
                          to={`/projects/${projectId}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {taskId != null ? 'Project' : 'Open'}
                        </Link>
                        {taskId == null && (
                          <>
                            <span className="text-ms-border">·</span>
                            <Link
                              to={timesheetReportHref(projectId)}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              Timesheet
                            </Link>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={() => markRead.mutate(n.id)}
                      disabled={markRead.isPending}
                      title="Mark as solved"
                      aria-label="Mark as solved"
                      className="inline-flex items-center justify-center min-w-[68px] h-8 px-3 rounded-md bg-emerald-600 text-white font-semibold text-[12px] hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Solve
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

